import { useEffect, useState } from 'react';

import { shouldUseUnitCompliantApi } from '@atlaskit/linking-common/units-rollout';

import { isPreviewPanelInUnitsRollout } from './units-rollout';

/**
 * Whether cross-unit resources must actually be isolated for the current organisation.
 *
 * Being in the rollout (see `isPreviewPanelUnitCheckEnabled`) only says preview panels *may* apply
 * unit isolation here. Whether they *must* depends on how far the organisation itself is through
 * the units migration, which the browser cannot infer: it is read from AGG's
 * `Query.admin_unitSettings` (backed by the units TCS key) as `endUsersLaunched` and
 * `boundaryEnforced`, both of which have to be true. An organisation part way through the
 * migration can have launched without enforcement yet, and in that state every site is still
 * visible to every user, so blocking a preview would hide something the user can legitimately
 * open.
 *
 * The read is owned by `shouldUseUnitCompliantApi` from `@atlaskit/linking-common/units-rollout`,
 * which re-checks the same gates (cheap and already exposed by this point), caches the AGG result
 * for the lifetime of the page, and resolves `false` for anything it cannot determine.
 *
 * Because that read is asynchronous this starts as `false`, i.e. no isolation, and flips to `true`
 * only once AGG confirms it. Failing open that way means a slow or failed lookup leaves previews
 * working exactly as they do today rather than blocking resources the user can actually see.
 */
export const useUnitIsolationEnabled = (): boolean => {
	const [isUnitIsolationEnabled, setIsUnitIsolationEnabled] = useState(false);

	useEffect(() => {
		let cancelled = false;

		shouldUseUnitCompliantApi(isPreviewPanelInUnitsRollout)
			.then((enabled) => {
				if (!cancelled) {
					setIsUnitIsolationEnabled(enabled);
				}
			})
			.catch(() => {
				// Fail open: leave isolation off, which is the pre-units behaviour.
			});

		return () => {
			cancelled = true;
		};
	}, []);

	return isUnitIsolationEnabled;
};
